/**
 * La campanita.
 *
 * QUÉ PROBLEMA RESUELVE. Hay cosas que le pasan a alguien mientras no está
 * mirando: se le cayó la inscripción por falta de comprobante, le aprobaron
 * el ingreso a un club, salió el cuadro del torneo. Sin un lugar donde
 * aparezcan, la única forma de enterarse es volver a abrir la pantalla justa
 * a ver si cambió algo.
 *
 * EL TEXTO SE ARMA ACÁ y no viene escrito del servidor. Del servidor llegan
 * el TIPO y los datos sueltos; la frase se escribe con el idioma de quien
 * mira. Mandarla hecha congelaría el idioma del día en que se creó el aviso,
 * y esta aplicación se lee en nueve.
 *
 * SE PREGUNTA CADA MINUTO, y no menos: un aviso que llega con un minuto de
 * atraso no le arruina el día a nadie, y una consulta cada cinco segundos por
 * cada pestaña abierta sí le arruina el día al servidor. Cuando la pestaña
 * está en segundo plano no se pregunta nada — quien no está mirando no
 * necesita el número al día.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Notification, NotificationsPage } from '@tennisup/contracts';

import { api } from '../lib/api';

/** Cada cuánto se le pregunta al servidor. Ver el comentario de arriba. */
export const POLL_EVERY_MS = 60_000;

export function NotificationBell(): JSX.Element | null {
  const { t } = useTranslation();
  const [page, setPage] = useState<NotificationsPage | null>(null);
  const [open, setOpen] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      setPage(await api.notifications());
    } catch {
      // Sin red o sin sesión: la campanita simplemente no aparece. Un error
      // rojo en la barra de arriba de TODAS las pantallas, por una consulta
      // de fondo, es peor que no mostrar el número.
    }
  }, []);

  useEffect(() => {
    void load();
    const reloj = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_EVERY_MS);
    return () => clearInterval(reloj);
  }, [load]);

  /**
   * Cerrar al tocar afuera.
   *
   * Sin esto, la lista queda abierta tapando la pantalla y hay que volver a
   * apuntarle a la campanita —que ahora está debajo de la propia lista— para
   * cerrarla.
   */
  useEffect(() => {
    if (!open) return undefined;
    function afuera(evento: MouseEvent): void {
      if (caja.current !== null && !caja.current.contains(evento.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', afuera);
    return () => document.removeEventListener('mousedown', afuera);
  }, [open]);

  if (page === null) return null;

  async function abrir(): Promise<void> {
    const abriendo = !open;
    setOpen(abriendo);
    // Se marcan leídas AL ABRIR, no al tocar cada una: la persona abre la
    // lista, la mira entera y ya la vio. Un botón por fila obliga a limpiar a
    // mano algo que se acaba de leer.
    if (abriendo && (page?.unread ?? 0) > 0) {
      try {
        setPage(await api.markNotificationsRead());
      } catch {
        // Que no se marquen no puede impedir leerlas.
      }
    }
  }

  return (
    <div className="bell-box" ref={caja}>
      <button
        type="button"
        className="bell"
        aria-expanded={open}
        aria-label={t('notifications.title')}
        onClick={() => void abrir()}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
          <path d="M12 2a1.5 1.5 0 0 1 1.5 1.5v.6A6 6 0 0 1 18 10v4l1.6 2.4a.6.6 0 0 1-.5.95H4.9a.6.6 0 0 1-.5-.95L6 14v-4a6 6 0 0 1 4.5-5.9v-.6A1.5 1.5 0 0 1 12 2Zm0 19a2.6 2.6 0 0 1-2.5-2h5A2.6 2.6 0 0 1 12 21Z" />
        </svg>
        {page.unread > 0 && (
          // El número, no un puntito: «tenés algo» y «tenés once cosas» son
          // dos situaciones distintas y la segunda apura.
          <span className="bell-count">{page.unread > 9 ? '9+' : page.unread}</span>
        )}
      </button>

      {open && (
        <div className="bell-list">
          {page.items.length === 0 ? (
            <p className="help">{t('notifications.empty')}</p>
          ) : (
            <ul>
              {page.items.map((item) => (
                <li key={item.id} className={item.readAt === null ? 'unread' : undefined}>
                  <NotificationText item={item} onNavigate={() => setOpen(false)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Un aviso: su frase, y el enlace a donde pasó si lo tiene. */
function NotificationText({
  item,
  onNavigate,
}: {
  item: Notification;
  onNavigate: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  // `item.data` viene del servidor y puede no traer todo lo que la frase
  // espera —una fila vieja, un tipo que cambió—. i18next deja el hueco en
  // blanco en vez de escribir «undefined», que es exactamente lo que se
  // quiere: un aviso incompleto se entiende, uno con «undefined» no.
  const texto = t(`notifications.kind.${item.kind}`, item.data);

  if (item.linkPath === null) return <span>{texto}</span>;
  return (
    <Link to={item.linkPath} onClick={onNavigate}>
      {texto}
    </Link>
  );
}
